import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { FileText, CheckCircle, XCircle, Clock, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('auth_token') || '';

const STATUS_COLORS = {
  pending: 'background:#fff8e1;color:#b7791f;border:1px solid #f6e05e',
  approved: 'background:#f0fff4;color:#276749;border:1px solid #9ae6b4',
  denied: 'background:#fff5f5;color:#c53030;border:1px solid #feb2b2',
};

export default function AdminMediaKitPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState({});
  const [approvedLinks, setApprovedLinks] = useState({});
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/media-kit/requests`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setWorking(w => ({ ...w, [id]: true }));
    try {
      const res = await fetch(`${API}/media-kit/approve/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      const full = `${window.location.origin}${data.access_url}`;
      setApprovedLinks(l => ({ ...l, [id]: full }));
      toast.success('Approved — access link generated');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setWorking(w => ({ ...w, [id]: false }));
    }
  };

  const deny = async (id) => {
    if (!window.confirm('Deny this request?')) return;
    setWorking(w => ({ ...w, [id]: true }));
    try {
      const res = await fetch(`${API}/media-kit/deny/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Request denied');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setWorking(w => ({ ...w, [id]: false }));
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const counts = { all: requests.length, pending: requests.filter(r => r.status === 'pending').length, approved: requests.filter(r => r.status === 'approved').length, denied: requests.filter(r => r.status === 'denied').length };

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={22} color="#0f2742" />
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f2742' }}>Media Kit Requests</h1>
          </div>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {['all', 'pending', 'approved', 'denied'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`mk-filter-${f}`}
              style={{ padding: '6px 14px', borderRadius: 999, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 13, fontWeight: filter === f ? 700 : 400, background: filter === f ? '#0f2742' : '#fff', color: filter === f ? '#fff' : '#667085' }}>
              {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#667085' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#667085', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
            No {filter === 'all' ? '' : filter} requests yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            {filtered.map(req => (
              <div key={req.id} data-testid={`mk-request-${req.id}`}
                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 16 }}>{req.first_name} {req.last_name}</strong>
                      <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, ...Object.fromEntries(STATUS_COLORS[req.status]?.split(';').map(s => s.split(':').map(x=>x.trim())).filter(a=>a.length===2)) }}>
                        {req.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#667085', marginTop: 4 }}>{req.work_email} · {req.company} · {req.job_title}</div>
                    {req.website && <div style={{ fontSize: 13, color: '#667085' }}>{req.website}</div>}
                  </div>
                  <div style={{ fontSize: 12, color: '#a0aec0', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={12} /> {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                  </div>
                </div>

                {/* Details */}
                <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
                  {req.intended_use && (
                    <div style={{ gridColumn: '1/-1' }}>
                      <span style={{ fontWeight: 600, color: '#0f2742' }}>Intended Use: </span>
                      <span style={{ color: '#4a5568' }}>{req.intended_use}</span>
                    </div>
                  )}
                  {req.advertising_interests && (
                    <div>
                      <span style={{ fontWeight: 600, color: '#0f2742' }}>Ad Interests: </span>
                      <span style={{ color: '#4a5568' }}>{req.advertising_interests}</span>
                    </div>
                  )}
                  {req.timeline && (
                    <div>
                      <span style={{ fontWeight: 600, color: '#0f2742' }}>Timeline: </span>
                      <span style={{ color: '#4a5568' }}>{req.timeline}</span>
                    </div>
                  )}
                  {req.additional_context && (
                    <div style={{ gridColumn: '1/-1' }}>
                      <span style={{ fontWeight: 600, color: '#0f2742' }}>Notes: </span>
                      <span style={{ color: '#4a5568' }}>{req.additional_context}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {req.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button onClick={() => approve(req.id)} disabled={working[req.id]} data-testid={`mk-approve-${req.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#276749', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: working[req.id] ? .6 : 1 }}>
                      <CheckCircle size={14} /> {working[req.id] ? 'Approving…' : 'Approve'}
                    </button>
                    <button onClick={() => deny(req.id)} disabled={working[req.id]} data-testid={`mk-deny-${req.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #feb2b2', background: '#fff5f5', color: '#c53030', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: working[req.id] ? .6 : 1 }}>
                      <XCircle size={14} /> Deny
                    </button>
                  </div>
                )}

                {/* Copy link after approval */}
                {(req.status === 'approved' || approvedLinks[req.id]) && approvedLinks[req.id] && (
                  <div style={{ marginTop: 14, background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#276749', fontWeight: 600 }}>Access Link (72h · one-time):</span>
                    <code style={{ fontSize: 11, color: '#276749', wordBreak: 'break-all', flex: 1 }}>{approvedLinks[req.id]}</code>
                    <button onClick={() => copyLink(approvedLinks[req.id])} data-testid={`mk-copy-link-${req.id}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid #9ae6b4', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#276749', whiteSpace: 'nowrap' }}>
                      <Copy size={12} /> Copy Link
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
